import React from 'react';
import ModalResponsive from '@/components/commons/modal-responsive';
import InputSingleFile from '@/components/sections/builder/settings/input-single-file';
import useToast from '@/components/ui/use-toast';
import Pagination from '@/components/commons/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import Image from 'next/image';
import clsx from 'clsx';
import { useGetAssets, useUploadImage } from '@/hooks/api/dashboard/file-service';

export interface ModalAssetProps {
  openModal?: boolean;
  setOpenModal?: (open: boolean) => void;
  onSelect?: (imageUrl: string) => void;
  initialValue?: string;
}

export default function ModalAsset({
  openModal,
  setOpenModal,
  onSelect,
  initialValue,
}: ModalAssetProps) {
  const { toast } = useToast();

  const [pagination, setPagination] = React.useState({ pageIndex: 0, pageSize: 50 });

  const queryAssetList = useGetAssets(
    { page: pagination.pageIndex + 1, pageSize: pagination.pageSize, q: '' },
    {
      enabled: openModal,
    }
  );

  const uploadMutation = useUploadImage({
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Gagal upload',
        description: 'Gambar gagal di upload.',
        duration: 1500,
      });
    },
    onSuccess() {
      queryAssetList.refetch();
    },
  });

  const handleUploadImage = async (file: Blob) => {
    const data = new FormData();
    data.append('file', file);

    uploadMutation.mutate(data);
  };

  const assets = queryAssetList.data?.data || [];
  const isLoadingAssets = queryAssetList.isLoading;
  const skeletonCount = 12;

  return (
    <ModalResponsive openModal={openModal} title='Select Image' setOpenModal={setOpenModal}>
      <div className='p-4 pt-0'>
        <InputSingleFile
          name='heroImage'
          type='image'
          onChange={(v) => {
            if (v) handleUploadImage(v.file as Blob);
          }}
          ratio='aspect-[10/1]'
          loading={uploadMutation.isPending}
        />
      </div>
      <div className='grid max-h-[420px] grid-cols-6 gap-4 overflow-y-auto p-4 pt-0'>
        {isLoadingAssets
          ? Array.from({ length: skeletonCount }).map((_, index) => (
              <div className='flex flex-col space-y-3' key={index}>
                <Skeleton className='h-[100px] w-[90px] rounded-sm p-2 ' />
                <div className='space-y-2'>
                  <Skeleton className='h-3 w-[90px]' />
                </div>
              </div>
            ))
          : assets.map((asset) => {
              const isSelected = initialValue === asset.file_url;
              if (asset.file_type === 'image') {
                return (
                  <button
                    type='button'
                    onClick={() => onSelect?.(asset.file_url)}
                    key={asset.id}
                    className={clsx(
                      'aspect-square rounded-sm p-2 hover:bg-slate-100 active:bg-slate-100',
                      isSelected && 'bg-slate-100'
                    )}
                  >
                    <Image
                      className='aspect-square w-full object-contain'
                      src={asset.file_url}
                      alt={asset.display_name}
                      width={150}
                      height={100}
                    />
                    <div className='mt-2 line-clamp-1 text-xs'>{asset.display_name}</div>
                  </button>
                );
              }

              return <></>;
            })}
      </div>
      <div className='p-3 '>
        <Pagination
          currentPage={pagination.pageIndex + 1}
          totalCount={queryAssetList.data?.totalCount || 0}
          pageSize={pagination.pageSize}
          onPageChange={(page) =>
            setPagination((prev) => ({
              ...prev,
              pageIndex: page - 1,
            }))
          }
          onPageSizeChange={(size) =>
            setPagination((prev) => ({
              ...prev,
              pageSize: size,
              pageIndex: 0,
            }))
          }
        />
      </div>
    </ModalResponsive>
  );
}
